import { useCallback, useEffect, useState } from 'react';

import type { HotelPlannerPort, HotelDetailsResult } from '@voll-intelligence/genui-contract';
import { loadHotelDetails, type HotelReadCache } from './hotel_read_cache.js';

/**
 * Estado do fetch do detalhe hotel-level. O drawer abre client-side (sem ida ao
 * agente) e busca lazy pelo port injetado, keyed no `offerId` opaco da oferta.
 * Mesma forma do `useFlightLegs`, com um contador de tentativa para o retry do
 * estado de erro. Sem port o hook nem tenta — quem decide não abrir é o chamador.
 * O port vem do chamador (a config do host), nunca de Context.
 */
export type HotelDetailsState =
  | { phase: 'loading' }
  | { phase: 'ready'; data: HotelDetailsResult }
  | { phase: 'error' };

/** O detalhe VAZIO do modo deferido — a forma mínima que o schema aceita, para
 *  a view nascer só do que a oferta carrega. */
const DEFERRED_DETAILS: HotelDetailsResult = { photos: [], amenities: [], reviews: [], rooms: [] };

/* Constantes de módulo: o estado sem resposta mantém a identidade entre renders. */
const DEFERRED_STATE: HotelDetailsState = { phase: 'ready', data: DEFERRED_DETAILS };
const LOADING_STATE: HotelDetailsState = { phase: 'loading' };
const ERROR_STATE: HotelDetailsState = { phase: 'error' };

type DetailsPort = Pick<HotelPlannerPort, 'getHotelDetails'>;

/** O pedido que uma leitura respondeu: trocar qualquer parte dele (outra oferta,
 *  nova tentativa, outro cache/port, o defer caindo) faz a leitura velha sair de
 *  cena no mesmo render. */
type DetailsRequest = {
  readonly offerId: string;
  readonly attempt: number;
  readonly cache: HotelReadCache;
  readonly port: DetailsPort | undefined;
  readonly defer: boolean;
};

const sameRequest = (a: DetailsRequest, b: DetailsRequest): boolean =>
  a.offerId === b.offerId &&
  a.attempt === b.attempt &&
  a.cache === b.cache &&
  a.port === b.port &&
  a.defer === b.defer;

/** Antes de qualquer resposta ao pedido corrente. Modo DEFERIDO (a página do hotel
 *  favorito antes do 1º Confirmar): a oferta é sintética e o handle não existe no
 *  servidor — buscar o detalhe seria um 404 garantido. A view nasce "pronta" só com
 *  o que a oferta carrega; o 1º Confirmar troca a oferta por uma real e o defer cai. */
function unansweredState(request: DetailsRequest): HotelDetailsState {
  if (request.defer) return DEFERRED_STATE;
  return request.port ? LOADING_STATE : ERROR_STATE;
}

export function useHotelDetails(
  offerId: string,
  cache: HotelReadCache,
  port: DetailsPort | undefined,
  options?: { defer?: boolean },
): {
  state: HotelDetailsState;
  retry: () => void;
} {
  const defer = options?.defer ?? false;
  const [attempt, setAttempt] = useState(0);
  const request: DetailsRequest = { offerId, attempt, cache, port, defer };
  const [answer, setAnswer] = useState<{
    request: DetailsRequest;
    state: HotelDetailsState;
  } | null>(null);
  const retry = useCallback(() => {
    cache.details.delete(offerId);
    setAttempt((previous) => previous + 1);
  }, [cache, offerId]);

  useEffect(() => {
    if (defer || !port) return undefined;
    const answered = { offerId, attempt, cache, port, defer };
    let cancelled = false;
    void Promise.resolve(loadHotelDetails(cache, port, offerId))
      .then((data) => {
        if (!cancelled) setAnswer({ request: answered, state: { phase: 'ready', data } });
      })
      .catch(() => {
        if (!cancelled) setAnswer({ request: answered, state: { phase: 'error' } });
      });
    return () => {
      cancelled = true;
    };
  }, [offerId, attempt, cache, port, defer]);

  // Um detalhe RESOLVIDO reescrito por fora (`patchHotelDetailsRate`, #3631):
  // a leitura já em tela troca pela reconciliada, sem voltar a `loading` nem
  // remontar — a página segue onde a pessoa a deixou.
  useEffect(() => {
    const onPatched = (patchedId: string) => {
      const cached = cache.details.get(offerId);
      if (patchedId !== offerId || !cached || cached instanceof Promise) return;
      setAnswer({
        request: { offerId, attempt, cache, port, defer },
        state: { phase: 'ready', data: cached },
      });
    };
    cache.detailListeners.add(onPatched);
    return () => {
      cache.detailListeners.delete(onPatched);
    };
  }, [cache, offerId, attempt, port, defer]);

  const state =
    answer && sameRequest(answer.request, request) ? answer.state : unansweredState(request);
  return { state, retry };
}
